import type { UserOnlineStatus } from 'generated/openapi/types.gen';
import { Heart, Play } from 'lucide-react';
import type { ComponentProps } from 'react';
import { memo, Suspense } from 'react';

import { useGetRecentLikers } from '@/features/likes/hooks/useGetRecentLikers';
import { useMyUserId } from '@/features/users/hooks/useGetMyUser';
import { Badge, type BadgeProps } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';

export interface ProfileStatusBadgeProps extends Omit<
  ComponentProps<'span'>,
  'children'
> {
  onlineStatus: UserOnlineStatus;
  isReadyToPlay: boolean;
  userId: string;
  badgeVariant?: BadgeProps['variant'];
}

/**
 * Priority is:
 * 1. Recently liked you
 * 2. Ready to play
 * 3. Online
 * 4. Recently Active
 */
export const ProfileStatusBadge = memo(function ProfileStatusBadge(
  props: ProfileStatusBadgeProps,
) {
  return (
    <Suspense fallback={<ProfileStatusBadgeSkeleton />}>
      <ProfileStatusBadgeContent {...props} />
    </Suspense>
  );
});

function ProfileStatusBadgeContent({
  onlineStatus,
  isReadyToPlay,
  userId,
  badgeVariant = 'secondary',
  ...props
}: ProfileStatusBadgeProps) {
  const myUserId = useMyUserId();

  const { data: recentlyLikedYou } = useGetRecentLikers({
    options: {
      select: ({ data: obfuscatedIds }) => {
        return obfuscatedIds.some(id => xorEqUuid(userId, myUserId, id));
      },
      gcTime: 5000,
      staleTime: 5000,
    },
  });

  if (recentlyLikedYou) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="prominent"
            className="before:animate-ping-subtle before:bg-positive-700 relative overflow-visible p-0 before:pointer-events-none before:absolute before:inset-0 before:rounded-md"
            {...props}
          >
            <div className="bg-positive-700 relative z-10 flex items-center gap-2 rounded-md px-3 py-0.5">
              <Heart className="size-3 fill-white" />
              <span>Liked you now</span>
            </div>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            Swiped right less than 10 minutes ago.
            <br />
            Like back to become Duos!
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (isReadyToPlay) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={badgeVariant} {...props}>
            <Play className="size-3" stroke="var(--color-positive-600)" />
            <span>Ready to play</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>Looking to play right now</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (onlineStatus === 'ONLINE') {
    return (
      <Badge variant={badgeVariant} {...props}>
        <div className="bg-positive-600 size-3 rounded-full" />
        <span>Online</span>
      </Badge>
    );
  }

  if (onlineStatus === 'RECENTLY_ONLINE') {
    return (
      <Badge variant={badgeVariant} {...props}>
        <div className="bg-positive-600 size-3 rounded-full" />
        <span>Recently active</span>
      </Badge>
    );
  }

  return null;
}

function ProfileStatusBadgeSkeleton() {
  return (
    <Badge variant="blur" className="w-22">
      <Skeleton className="size-3 rounded-full" />
    </Badge>
  );
}

const xorEqUuid = (a: string, b: string, c: string): boolean => {
  const uuidToBigInt = (s: string) => BigInt('0x' + s.replaceAll('-', ''));

  return (uuidToBigInt(a) ^ uuidToBigInt(b) ^ uuidToBigInt(c)) === 0n;
};
